/**
 * @author: lv.chen
 * @date: 2025/7/8
 * @desc: Market Profile页面Global Performance模块组件
 */
import React, { useRef, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { cloneDeep } from 'lodash-es';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import ChartBubble from '@intelligence/components/ChartBubble';
import DataSourceExplanation from '@intelligence/v3/components/DataSourceExplanation';
import MapChart from '@intelligence/v3/components/ChartComponents/MapChart';
import LineChart from '@intelligence/v3/components/ChartComponents/LineChart';
import { toDateTypeFilter } from '@intelligence/utils/common';
import { GRANULARITY_MOMENT_HELPER_MAPPING } from '@intelligence/utils/dateUtils';
import { formatNum } from '@intelligence/utils/commonUtils';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { sortArrDesc } from '@intelligence/pages/MarketProfile/_util';
import { formatMessageCountryNameByNameEN, getAllCountriesByRegions } from '@intelligence/v3/utils/region';
import { ChartType } from '@intelligence/v3/enums/chart';

import './index.scss';

/**
 * Market Profile页面Global Performance模块组件props类型
 */
interface IGlobalPerformanceProps extends IBaseProps {
  /**
   * 业务domain
   */
  domain: string;
  /**
   * 是否加载数据中
   */
  loading: boolean;
  /**
   * 是否无数据
   */
  noData: boolean;
  /**
   * 数据源差异解释数据源配置
   */
  summarySourceKeyList?: string[];
  /**
   * 数据源差异解释内容配置
   */
  summarySourceList?: string[][];
  /**
   * HTML模式下，数据源名称列表（用于展示数据源图标和标题）
   * 例如：['MScience', 'NPD', 'GSD']
   */
  htmlDataSourceList: string[];
  /**
   * HTML模式下，数据源HTML内容映射
   * 例如：{ MScience: 'Provides <span class="highlight">...</span>', ... }
   */
  htmlContentMap: IRecord<string>;
  /**
   * 国家映射
   * 键：国家code
   * 值：国家
   */
  countryMap: IRecord<IRegionCountryEntity> | null;
  /**
   * 地区国家列表
   */
  regionOptions: IRegion[] | null;
  /**
   * 当前勾选的国家
   */
  countries: string[];
  /**
   * 当前图表类型
   */
  chartType: ChartType;
  /**
   * 切换当前图表类型的回调
   * @param chartType 图表类型
   */
  onChartTypeChange?: (chartType: ChartType) => void;
  /**
   * 当前指标
   */
  metric: IPrimaryKeyMetric;
  /**
   * 当前指标名称
   */
  metricName: string;
  /**
   * echarts世界地图数据
   */
  mapData: IMapChartData[] | null;
  /**
   * echarts折线图数据
   */
  lineData: ILineChartData[] | null;
  /**
   * 指标筛选器UI
   */
  metricSelect: React.ReactElement;
}

/**
 * Card组件右上角的操作按钮列表
 */
const iconList = [
  /**
   * 世界地图
   */
  { id: 1, value: ChartType.Map, context: 'dbi_world_map_chart', icon: 'chart-map' },
  /**
   * 折线图
   */
  { id: 2, value: ChartType.Line, context: 'dbi_line_chart', icon: 'discount-chart' },
];

const GlobalPerformance: React.FC<IGlobalPerformanceProps> = (props) => {
  const {
    style,
    className,
    domain,
    loading = true,
    noData = true,
    htmlDataSourceList,
    htmlContentMap,
    countryMap,
    regionOptions,
    countries,
    chartType,
    onChartTypeChange,
    metric,
    metricName,
    mapData,
    lineData,
    metricSelect,
    isExplanation = false,
  } = props;

  const { locale, formatMessage } = useIntl();

  const dateType = toDateTypeFilter[metric?.granularity] || 'daily';

  /**
   * 数据源差异解释说明的触发容器dom
   */
  const dataSourceExplanationTrigger = useRef<HTMLDivElement | null>(null);

  /**
   * 数据源差异解释说明的展示容器dom
   */
  const dataSourceExplanationBody = useRef<HTMLDivElement | null>(null);

  /**
   * 国家列表
   */
  const allCountries = useMemo<ICountry[]>(() => {
    return getAllCountriesByRegions(regionOptions);
  }, [regionOptions]);

  /**
   * 翻译国家名称
   * @param countryCode 国家code
   * @return 当前语言环境下的国家名称
   */
  const formatMessageCountryNameByCode = (countryCode?: string) => {
    if (!countryCode) return '';
    const countryCodeLowerCase = countryCode.toLowerCase();
    const country = countryMap?.[countryCodeLowerCase] || ({} as unknown as IRegionCountryEntity);
    return getOptionText(locale, {
      value: countryCode,
      text: countryCode,
      textEN: country?.country_name_en || '',
      textCN: country?.country_name_cn || '',
    });
  };

  /**
   * echarts世界地图tooltip气泡formatter
   * @param params 世界地图数据
   */
  const mapTooltipFormatter = (params: any) => {
    const countryName = params?.data?.name || params?.name || '';
    const countryNameNew = formatMessageCountryNameByNameEN(locale, {
      countries: allCountries,
      countryNameEN: countryName,
    });
    const paramsNew = {
      ...params,
      data: {
        ...params?.data,
        ...params?.data?.payload,
        name: countryNameNew,
      },
      name: countryNameNew,
    };

    return ChartBubble({
      topChartsType: 'marketProfile-map',
      ifNoBarImg: true,
      imageType: 'mobile',
      titleNow: '',
      headerLeftTitle: metricName,
      leftContent: formatMessage({ id: 'dbi_market_total_game' }),
      dateType,
      data: paramsNew,
    });
  };

  /**
   * echarts折线图tooltip气泡formatter
   * @param params 折线图数据
   */
  const lineTooltipFormatter = (params: any) => {
    const newParams = params
      ?.map((item: any) => {
        const seriesName = item?.seriesName;
        const newSeriesName = formatMessageCountryNameByCode(seriesName);
        return { ...item, seriesName: newSeriesName };
      })
      ?.sort(sortArrDesc('value'));

    return ChartBubble({
      topChartsType: 'marketProfile-line',
      chartType: 'line',
      ifNoBarImg: true,
      imageType: 'mobile',
      titleNow: metricName,
      headerLeftTitle: '',
      dateType,
      data: newParams || [],
    });
  };

  /**
   * 按countries（即当前勾选的国家）对series排序
   * @param series echarts图表的series配置
   * @return echarts图表的series配置（排序）
   */
  const getSortedSeries = (series: ILineSeriesOption[]): ILineSeriesOption[] => {
    if (!Array.isArray(series) || series.length === 0) return [];
    const sortedSeries = cloneDeep(series);

    sortedSeries.sort((a, b) => {
      const aIndex = countries?.indexOf(a?.name as any) ?? -1;
      const bIndex = countries?.indexOf(b?.name as any) ?? -1;
      return aIndex - bIndex;
    });

    return sortedSeries;
  };

  /**
   * 组件的刷新依赖项
   */
  const updateDeps = useMemo<React.DependencyList>(() => [], [countryMap, countries, metricName, dateType]);

  return (
    <CardSlotV2
      style={style}
      className={classnames('dbi-market-profile-global-performance', className)}
      title={
        <div className="dbi-market-profile-global-performance__title">
          <div>{formatMessage({ id: 'dbi_market_global_perf' })}</div>
          <div ref={dataSourceExplanationTrigger}></div>
        </div>
      }
      iconList={iconList}
      activeTab={chartType}
      getChange={onChartTypeChange}
    >
      {loading && (
        <div className="dbi-market-profile-global-performance__loading-wrapper" style={{ fontSize: 14, height: 410 }}>
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      {!loading && noData && (
        <div style={{ fontSize: 14, height: 410 }}>
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      )}
      {!loading && !noData && (
        <>
          {!isExplanation && <DataSourceExplanation
            domain={domain}
            dataSourceList={htmlDataSourceList}
            htmlContentMap={htmlContentMap}
            trigger={dataSourceExplanationTrigger}
            body={dataSourceExplanationBody}
          />}
          <div ref={dataSourceExplanationBody}></div>
          {/* 指标筛选器 */}
          {React.isValidElement(metricSelect) && metricSelect}
          <div className="dbi-market-profile-global-performance__chart-wrapper">
            {/* 世界地图 */}
            {chartType === ChartType.Map && (
              <MapChart
                locale={locale}
                loading={loading}
                regionOptions={regionOptions}
                data={mapData}
                tooltipFormatter={mapTooltipFormatter}
                updateDeps={updateDeps}
              />
            )}
            {/* 折线图 */}
            {chartType === ChartType.Line && (
              <LineChart
                locale={locale}
                loading={loading}
                data={lineData}
                tooltipFormatter={lineTooltipFormatter}
                yAxisLabelFormatter={(value: number) => formatNum(value)}
                xAxisLabelFormatter={(value: string) => {
                  const dateFormat = GRANULARITY_MOMENT_HELPER_MAPPING[metric?.granularity]?.format || 'YYYY-MM-DD';
                  return formatDateStr(value, dateFormat);
                }}
                legendFormatter={formatMessageCountryNameByCode}
                getSortedSeries={getSortedSeries}
                updateDeps={updateDeps}
              />
            )}
          </div>
        </>
      )}
    </CardSlotV2>
  );
};

export default GlobalPerformance;
